import type { ReactNode } from 'react'
import {
  CompanyIcon,
  ceilingVerdict,
  type Verdict,
  VerdictPill,
} from '../../shared/pills'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type { CompanyPacingRow, PacingData, ReviewComment } from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * Delivery section (spec §8.5, behavioral only) — repurposes the shared
 * `PacingData` shape as "Delivery" (A1's note on `behavioral.ts`: total ->
 * ANSWER LENGTH, companyRows -> target range rows, topicAvg -> BEHAVIORAL
 * AVG). FILLER WORDS / SPEAKING PACE aren't modeled on `ReviewSubmission` —
 * fixed mock values owned here. All five tiles are clickable (spec §8.5).
 */
const FILLER_WORDS = 14
const FILLER_TARGET = 8
const SPEAKING_PACE = 168
const PACE_MIN = 140
const PACE_MAX = 170
const PACE_RANGE = `${PACE_MIN}–${PACE_MAX}`

// Verdicts derive from the constants above so pill and number can't
// silently diverge. Filler words use the ceiling-based thresholds; pace is a
// band, not a ceiling, so it gets its own in/out-of-range check.
const FILLER_VERDICT: Verdict = ceilingVerdict(FILLER_WORDS, FILLER_TARGET)
const PACE_VERDICT: Verdict =
  SPEAKING_PACE >= PACE_MIN && SPEAKING_PACE <= PACE_MAX
    ? 'on-target'
    : 'over-target'

export function DeliverySection(props: {
  pacing: PacingData
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { pacing, comments = [], anchorRef } = props
  const { openComposer } = useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'delivery')

  return (
    <SectionCard
      title="Delivery"
      commentCount={sectionComments.length}
      onAddComment={() =>
        openComposer('delivery', 'Delivery · Overall', 'critique')
      }
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      <div className="mb-2.5 grid grid-cols-[1fr_1.6fr] gap-2.5">
        <button
          type="button"
          onClick={() =>
            openComposer('delivery', 'Delivery · Length', 'critique')
          }
          className="rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-3 text-left"
        >
          <div className="mb-0.5 text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
            ANSWER LENGTH
          </div>
          <div className="font-mono text-[22px] font-bold text-(--cd-text)">
            {pacing.total}
          </div>
          <div className="mt-0.5 text-[11px] text-(--cd-text-faint)">
            {pacing.totalNote}
          </div>
        </button>

        <div className="flex flex-col gap-1.5 rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-2.5">
          <div className="text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
            {pacing.companyRows.length > 1
              ? 'COMPANY TARGET RANGES'
              : 'TARGET RANGE'}
          </div>
          {pacing.companyRows.map((row) => (
            <button
              key={row.company}
              type="button"
              onClick={() =>
                openComposer(
                  'delivery',
                  `Delivery · ${row.company} target`,
                  'critique',
                )
              }
              className="flex items-center gap-2.5 rounded-md px-1 py-1 text-left hover:bg-black/10"
            >
              <CompanyIcon company={row.company} />
              <span className="w-[70px] shrink-0 text-[12.5px] font-semibold text-(--cd-text)">
                {row.company}
              </span>
              <span className="w-[84px] shrink-0 font-mono text-xs text-(--cd-text-muted)">
                {row.range}
              </span>
              <VerdictPill verdict={VERDICT_KEY[row.verdict]} />
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={() =>
            openComposer('delivery', 'Delivery · Filler words', 'critique')
          }
          className="flex-1 rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-2.5 text-left"
        >
          <div className="flex items-baseline justify-between">
            <div className="text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
              FILLER WORDS
            </div>
            <VerdictPill verdict={FILLER_VERDICT} />
          </div>
          <div className="mt-1 text-base font-bold text-(--cd-text)">
            {FILLER_WORDS}{' '}
            <span className="text-[11px] font-medium text-(--cd-text-faint)">
              um / uh · target ≤ {FILLER_TARGET}
            </span>
          </div>
        </button>
        <button
          type="button"
          onClick={() =>
            openComposer('delivery', 'Delivery · Pace', 'critique')
          }
          className="flex-1 rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-2.5 text-left"
        >
          <div className="flex items-baseline justify-between">
            <div className="text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
              SPEAKING PACE
            </div>
            <VerdictPill verdict={PACE_VERDICT} />
          </div>
          <div className="mt-1 text-base font-bold text-(--cd-text)">
            {SPEAKING_PACE} wpm{' '}
            <span className="text-[11px] font-medium text-(--cd-text-faint)">
              target {PACE_RANGE}
            </span>
          </div>
        </button>
        {pacing.topicAvg && (
          <button
            type="button"
            onClick={() =>
              openComposer('delivery', 'Delivery · Behavioral avg', 'critique')
            }
            className="flex-1 rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-2.5 text-left"
          >
            <div className="mb-0.5 text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
              {pacing.topicAvg.label}
            </div>
            <div className="text-base font-bold text-(--cd-text)">
              {pacing.topicAvg.value}{' '}
              <span
                className="text-[11px] font-semibold"
                style={{
                  color: pacing.topicAvg.over
                    ? 'var(--cd-amber)'
                    : 'var(--cd-green)',
                }}
              >
                {pacing.topicAvg.delta}
              </span>
            </div>
          </button>
        )}
      </div>
    </SectionCard>
  )
}

const VERDICT_KEY: Record<CompanyPacingRow['verdict'], Verdict> = {
  Faster: 'faster',
  'In range': 'in-range',
  'Over range': 'over-range',
  'Far over': 'far-over',
}
